import React, { useEffect, useState, useCallback } from 'react';
import '../super-admin.css';
import { Typography, Card, Form, Input, Select, Switch, InputNumber, Button, message, Row, Col } from 'antd';
import { SettingOutlined, MailOutlined } from '@ant-design/icons';
import { API_BASE as apiBase, authHeaders } from '../../config/api';

const { Title } = Typography;

const localeOptions = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'fr-FR', label: 'Français (FR)' },
  { value: 'ar', label: 'العربية' },
  { value: 'es-ES', label: 'Español (ES)' },
  { value: 'de-DE', label: 'Deutsch (DE)' },
];

const SystemConfigs = () => {
  const [config, setConfig] = useState({});
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load system configuration from API
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/admin/system-config`, {
        headers: { ...authHeaders() }
      });
      if (!res.ok) throw new Error('Failed to load system configuration');
      const data = await res.json();
      setConfig(data);
      form.setFieldsValue(data);
    } catch (err) {
      message.error(err.message || 'Error loading system configuration');
      // Set default values if API fails
      const defaultConfig = {
        siteName: 'Asuma Platform',
        defaultLocale: 'en-US',
        maintenanceMode: false,
        sessionTimeoutMin: 60,
        emailFrom: 'noreply@asuma.app',
        smtpHost: 'smtp.example.com',
        smtpPort: 587,
        smtpSecure: true,
        maxFileSize: 10,
        allowRegistration: true,
      };
      setConfig(defaultConfig);
      form.setFieldsValue(defaultConfig);
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Save system configuration to API
  const onSave = async (values) => {
    setSaving(true);
    try {
      console.log('[CONFIG Frontend] Saving configuration:', values);
      const res = await fetch(`${apiBase}/admin/system-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(values)
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to save system configuration');
      }
      const updatedConfig = await res.json();
      setConfig(updatedConfig);
      message.success('System configuration saved successfully!');
      console.log('[CONFIG Frontend] Configuration saved successfully:', updatedConfig);
    } catch (err) {
      console.error('[CONFIG Frontend] Error saving configuration:', err);
      message.error(err.message || 'Error saving system configuration');
    } finally {
      setSaving(false);
    }
  };

  // Reset to current saved values
  const onReset = () => {
    form.setFieldsValue(config);
    message.info('Form reset to saved configuration');
  };

  return (
    <div className="sa-content">
      <Title level={2} style={{ marginBottom: 24 }}>System Configuration</Title>

      <Row gutter={[16, 16]}>
        {/* General Settings Card */}
        <Col xs={24} lg={12}>
          <Card 
            hoverable 
            style={{ borderRadius: 14 }} 
            bodyStyle={{ padding: 20 }}
            loading={loading}
          >
            {/* Header with settings icon */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ 
                  width: 44, 
                  height: 44, 
                  borderRadius: 10, 
                  background: '#3730a322', 
                  color: '#3730a3', 
                  display: 'grid', 
                  placeItems: 'center', 
                  fontSize: 20 
                }}>
                  <SettingOutlined />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>General Settings</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>Basic platform configuration</div>
                </div>
              </div>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onSave}
              style={{ height: '100%' }}
            >
              <Form.Item 
                label="Site Name" 
                name="siteName" 
                rules={[{ required: true, message: 'Please enter site name' }]}
              > 
                <Input placeholder="Enter site name" />
              </Form.Item>

              <Form.Item 
                label="Default Locale" 
                name="defaultLocale" 
                rules={[{ required: true }]}
              > 
                <Select options={localeOptions} placeholder="Select default language" />
              </Form.Item>

              <Form.Item 
                label="Session Timeout (minutes)" 
                name="sessionTimeoutMin" 
                rules={[{ required: true }]}
              > 
                <InputNumber 
                  min={5} 
                  max={720} 
                  style={{ width: '100%' }}
                  placeholder="Session timeout in minutes"
                />
              </Form.Item>

              <Form.Item 
                label="Maximum File Size (MB)" 
                name="maxFileSize" 
                rules={[{ required: true }]}
              > 
                <InputNumber 
                  min={1} 
                  max={100} 
                  style={{ width: '100%' }}
                  placeholder="Max file upload size"
                />
              </Form.Item>

              <div style={{ 
                padding: '12px 16px', 
                background: '#f8fafc', 
                borderRadius: 8, 
                marginBottom: 16 
              }}>
                <Form.Item 
                  label="Maintenance Mode" 
                  name="maintenanceMode" 
                  valuePropName="checked"
                  style={{ marginBottom: 8 }}
                >
                  <Switch />
                </Form.Item>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Enable to put the platform in maintenance mode
                </div>
              </div>

              <div style={{ 
                padding: '12px 16px', 
                background: '#f8fafc', 
                borderRadius: 8, 
                marginBottom: 16 
              }}>
                <Form.Item 
                  label="Allow User Registration" 
                  name="allowRegistration" 
                  valuePropName="checked"
                  style={{ marginBottom: 8 }}
                >
                  <Switch />
                </Form.Item>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Allow new users to register accounts
                </div>
              </div>

              {/* Email Configuration Section */}
              <div style={{ 
                padding: '12px 16px', 
                background: '#f0f9ff', 
                borderRadius: 8, 
                marginBottom: 16,
                border: '1px solid #e0f2fe'
              }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>
                  📧 Email Configuration
                </div>
                
                <Form.Item 
                  label="Email From Address" 
                  name="emailFrom" 
                  rules={[
                    { type: 'email', message: 'Enter a valid email' }, 
                    { required: true }
                  ]}
                > 
                  <Input placeholder="noreply@example.com" />
                </Form.Item>

                <Form.Item 
                  label="SMTP Host" 
                  name="smtpHost" 
                  rules={[{ required: true }]}
                > 
                  <Input placeholder="smtp.example.com" />
                </Form.Item>

                <Form.Item 
                  label="SMTP Port" 
                  name="smtpPort" 
                  rules={[{ required: true }]}
                > 
                  <InputNumber 
                    min={1} 
                    max={65535} 
                    style={{ width: '100%' }}
                    placeholder="587"
                  />
                </Form.Item>

                <Form.Item 
                  label="Use Secure Connection (TLS/SSL)" 
                  name="smtpSecure" 
                  valuePropName="checked"
                  style={{ marginBottom: 8 }}
                >
                  <Switch />
                </Form.Item>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  Enable secure connection for SMTP
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: 8, 
                flexWrap: 'wrap',
                marginTop: 24
              }}>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={saving}
                  style={{ flex: 1, minWidth: 'auto' }}
                >
                  Save Configuration
                </Button>
                <Button onClick={onReset} disabled={saving}>
                  Reset
                </Button>
                <Button onClick={loadConfig} disabled={loading || saving}>
                  Reload
                </Button>
              </div>
            </Form>
          </Card>
        </Col>

        {/* Email Settings Card */}
        <Col xs={24} lg={12}>
          <Card 
            hoverable 
            style={{ borderRadius: 14 }} 
            bodyStyle={{ padding: 20 }}
            loading={loading}
          >
            {/* Header with email icon */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ 
                  width: 44, 
                  height: 44, 
                  borderRadius: 10, 
                  background: '#05966922', 
                  color: '#059669', 
                  display: 'grid', 
                  placeItems: 'center', 
                  fontSize: 20 
                }}>
                  <MailOutlined />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Email Configuration</div>
                  <div style={{ color: '#64748b', fontSize: 13 }}>SMTP and email settings</div>
                </div>
              </div>
            </div>

            <div style={{ 
              padding: '12px 16px', 
              background: '#eff6ff', 
              borderRadius: 8, 
              marginBottom: 16,
              border: '1px solid #dbeafe'
            }}>
              <div style={{ fontSize: 13, color: '#1e40af', marginBottom: 4 }}>
                <strong>Email Configuration</strong>
              </div>
              <div style={{ fontSize: 12, color: '#1e3a8a', lineHeight: 1.4 }}>
                Configure SMTP settings for sending system emails, notifications, and password resets.
              </div>
            </div>

            {/* Email form fields - these are part of the main form */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                Email From Address
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                Current: {config.emailFrom || 'Not set'}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>
                SMTP Configuration
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                Host: {config.smtpHost || 'Not set'}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                Port: {config.smtpPort || 'Not set'}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                Secure: {config.smtpSecure ? 'Enabled' : 'Disabled'}
              </div>
            </div>

            {/* Note about form location */}
            <div style={{ 
              padding: '12px 16px', 
              background: '#fef3c7', 
              borderRadius: 8, 
              marginBottom: 16,
              border: '1px solid #fcd34d'
            }}>
              <div style={{ fontSize: 13, color: '#92400e', marginBottom: 4 }}>
                <strong>Note</strong>
              </div>
              <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.4 }}>
                Email settings are configured in the General Settings form on the left. Use the form there to update email configuration.
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SystemConfigs;
